import { SegmentedControl } from "@/components/ui/segmented-control";
import { useIsMobile } from "@/hooks/use-mobile";
import { RefreshCw, Download, ChevronDown } from "lucide-react";
import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";
import {
  MobileFilterButton,
  MobileFilterPanel,
} from "../layout/MobileFilterPanel";
import { HelpHint } from "../misc/HelpHint";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CanAccess, useDataProvider } from "ra-core";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { CrmDataProvider } from "../providers/types";
import { buildAnalytics, type AnalyticsFilter } from "./analyticsModel";
import { FINANCIAL_ZONES } from "./financeModel";
import { rubles, EXPENSE_REASONS, expenseCategoryLabel } from "./expenseModel";
import { ADJUSTMENT_REASONS } from "./adjustmentModel";
export function FinancialAnalytics() {
  return (
    <CanAccess
      resource="analytics"
      action="list"
      accessDenied={
        <p className="p-6" role="alert">
          Аналитика доступна только администратору.
        </p>
      }
    >
      <AnalyticsReport />
    </CanAccess>
  );
}
function AnalyticsReport() {
  const isMobile = useIsMobile();
  const Content = isMobile ? MobileContent : "div";
  const [filtersOpen, setFiltersOpen] = useState(false);
  const provider = useDataProvider<CrmDataProvider>();
  const [start, setStart] = useState(""),
    [end, setEnd] = useState(""),
    [project, setProject] = useState(""),
    [filter, setFilter] = useState<AnalyticsFilter>({
      start: null,
      end: null,
      deal_id: null,
    }),
    [validation, setValidation] = useState(""),
    [tab, setTab] = useState("projects"),
    [page, setPage] = useState(1),
    [adjustmentPage, setAdjustmentPage] = useState(1);
  const query = useQuery({
    queryKey: ["financial_analytics", filter],
    queryFn: () => provider.getFinancialAnalytics(filter),
  });
  const options = useQuery({
    queryKey: ["financial_analytics", "project-options"],
    queryFn: () =>
      provider.getFinancialAnalytics({ start: null, end: null, deal_id: null }),
    select: (data) => data.projects,
  });
  const data = query.data,
    report = data ? buildAnalytics(data, filter) : null;
  const reasonLabel = (reason: string) =>
    [...EXPENSE_REASONS, ...ADJUSTMENT_REASONS].find((r) => r.value === reason)
      ?.label ?? reason;
  const apply = () => {
    if (start && end && start > end) {
      setValidation("Начало периода должно быть раньше окончания");
      return;
    }
    setValidation("");
    setFiltersOpen(false);
    setPage(1);
    setAdjustmentPage(1);
    setFilter({
      start: start || null,
      end: end || null,
      deal_id: project ? Number(project) : null,
    });
  };
  const totalPrice =
    report?.projects.reduce((s, p) => s + Number(p.plan?.price ?? 0), 0) ?? 0;
  const allSpent =
    report?.projects.reduce((s, p) => s + Math.round(p.spent * 100), 0) ?? 0;
  const exportCsv = () => {
    if (!report) return;
    const rows = [
      [
        "Проект",
        "Цена КП в плане",
        "Бюджет после корректировок",
        "Подтверждённые расходы за всё время",
        "Остаток КП до налогов",
        "Архив",
      ],
      ...report.projects.map((p) => [
        p.name,
        p.plan?.price ?? "",
        p.plan ? p.budget : "",
        p.plan ? p.spent : "",
        p.remainder ?? "",
        p.archived_at ? "Да" : "Нет",
      ]),
    ];
    const csv =
      "\ufeff" +
      rows
        .map((r) =>
          r.map((v) => '"' + String(v).replaceAll('"', '""') + '"').join(";"),
        )
        .join("\r\n");
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = "ROVERO-проекты.csv";
    link.click();
    URL.revokeObjectURL(url);
  };
  const titles = [
    ["projects", "Проекты"],
    ["zones", "Зоны"],
    ["reasons", "Причины"],
    ["parties", "Сотрудники и подрядчики"],
    ["operations", "Операции"],
  ];
  return (
    <>
      {isMobile && (
        <MobileHeader
          title="Аналитика"
          actions={
            <>
              <MobileFilterButton
                label="Фильтры аналитики"
                count={
                  Number(!!filter.start || !!filter.end) +
                  Number(filter.deal_id != null)
                }
                onClick={() => setFiltersOpen(true)}
              />
            </>
          }
        />
      )}
      <Content
        className={
          isMobile
            ? "space-y-5"
            : "max-w-7xl mx-auto p-4 sm:p-6 space-y-5 pb-24"
        }
      >
        {isMobile && (
          <div className="flex justify-end">
            {" "}
            <Button
              variant="outline"
              aria-label="Экспорт проектов CSV"
              disabled={!report || query.isFetching || !!query.error}
              onClick={exportCsv}
            >
              <Download className="size-4" />
              Экспорт CSV
            </Button>
          </div>
        )}
        {!isMobile && (
          <div className="flex flex-wrap justify-between items-center gap-3">
            <h1 className="text-xl sm:text-2xl font-semibold">
              Аналитика ROVERO
            </h1>
            <div className="flex gap-2">
              <Button
                variant="outline"
                disabled={query.isFetching}
                aria-label="Обновить отчёт"
                className="h-11 w-11 sm:w-auto rounded-xl"
                onClick={() => query.refetch()}
              >
                <RefreshCw className="size-4" />
                <span className="hidden sm:inline">Обновить отчёт</span>
              </Button>
              <Button
                variant="outline"
                disabled={!report || query.isFetching || !!query.error}
                aria-label="Экспорт проектов CSV"
                className="h-11 w-11 sm:w-auto rounded-xl"
                onClick={exportCsv}
              >
                <Download className="size-4" />
                <span className="hidden sm:inline">Экспорт CSV</span>
              </Button>
            </div>
          </div>
        )}
        {isMobile ? (
          <MobileFilterPanel
            title="Фильтры аналитики"
            open={filtersOpen}
            onOpenChange={setFiltersOpen}
            onReset={() => {
              setStart("");
              setEnd("");
              setProject("");
            }}
            onApply={apply}
            applyLabel="Показать отчёт"
          >
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  const d = new Date();
                  setStart(
                    d.getFullYear() +
                      "-" +
                      String(d.getMonth() + 1).padStart(2, "0") +
                      "-01",
                  );
                  setEnd("");
                }}
              >
                Этот месяц
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setStart("");
                  setEnd("");
                  setProject("");
                }}
              >
                Всё время
              </Button>
            </div>
            <div className="grid sm:grid-cols-4 gap-4 rounded-xl sm:border sm:bg-card sm:p-4">
              <label className="text-sm">
                С даты
                <Input
                  type="date"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                />
              </label>
              <label className="text-sm">
                По дату
                <Input
                  type="date"
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                />
              </label>
              <label className="text-sm">
                Проект
                <select
                  className="block border rounded-md bg-background h-11 sm:h-9 w-full px-2"
                  value={project}
                  onChange={(e) => setProject(e.target.value)}
                >
                  <option value="">Все проекты, включая архив</option>
                  {options.data?.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.archived_at ? " (архив)" : ""}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {validation && (
              <p role="alert" className="text-sm text-destructive">
                {validation}
              </p>
            )}
          </MobileFilterPanel>
        ) : (
          <div className="grid sm:grid-cols-4 gap-4 rounded-xl sm:border sm:bg-card sm:p-4">
            <label className="text-sm">
              С даты
              <Input
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </label>
            <label className="text-sm">
              По дату
              <Input
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
              />
            </label>
            <label className="text-sm">
              Проект
              <select
                className="block border rounded-md bg-background h-11 sm:h-9 w-full px-2"
                value={project}
                onChange={(e) => setProject(e.target.value)}
              >
                <option value="">Все проекты, включая архив</option>
                {options.data?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.archived_at ? " (архив)" : ""}
                  </option>
                ))}
              </select>
            </label>
            <Button className="self-end" onClick={apply}>
              Показать отчёт
            </Button>
          </div>
        )}
        {validation && (
          <p role="alert" className="text-destructive">
            {validation}
          </p>
        )}
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <span>
            {filter.start || filter.end
              ? `${filter.start ?? "Начало учёта"} — ${filter.end ?? "Сегодня"}`
              : "Весь период"}
            {filter.deal_id != null
              ? " · Выбран один проект"
              : " · Все проекты"}
          </span>
          <HelpHint>
            Расходы отбираются по дате расхода, корректировки и завершённые
            задачи — по дате операции (Москва). Бюджеты проектов показывают
            текущее состояние за всё время.
          </HelpHint>
        </div>
        {query.isPending ? (
          <p>Загрузка аналитики…</p>
        ) : query.error ? (
          <p role="alert">Не удалось загрузить отчёт. Обновите его.</p>
        ) : (
          report &&
          data && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  [
                    "Проекты с планом",
                    report.projects.filter((p) => p.plan).length +
                      " / " +
                      report.projects.length,
                  ],
                  ["Сумма КП в планах", rubles(totalPrice)],
                  ["Расходы за период", rubles(report.spent)],
                  [
                    "Корректировки за период, нетто",
                    rubles(report.adjustmentsNet),
                  ],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="border rounded-2xl bg-card p-3 sm:p-4"
                  >
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className="text-base sm:text-xl font-semibold tabular-nums mt-2 break-words">
                      {value}
                    </p>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between gap-3 rounded-xl border bg-card p-4">
                <div>
                  <p className="text-xs text-muted-foreground">
                    Расчётный остаток КП
                  </p>
                  <strong className="text-lg tabular-nums">
                    {rubles(totalPrice - allSpent / 100)}
                  </strong>
                </div>
                <HelpHint>
                  Остаток до налогов, комиссий и ещё не подтверждённых расходов.
                  Оплата клиентом здесь не учитывается. Компенсации отражаются
                  без повторного списания. Расходы за период включают все
                  подтверждённые заявки.
                </HelpHint>
              </div>
              {isMobile ? (
                <label className="block text-xs font-medium text-muted-foreground">
                  Раздел отчёта
                  <select
                    aria-label="Раздел отчёта"
                    className="mt-2 block h-11 w-full rounded-xl border bg-card px-3 text-sm font-medium text-foreground"
                    value={tab}
                    onChange={(event) => {
                      setTab(event.target.value);
                      setPage(1);
                      setAdjustmentPage(1);
                    }}
                  >
                    {titles.map(([key, title]) => (
                      <option key={key} value={key}>
                        {title}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <SegmentedControl
                  label="Раздел отчёта"
                  value={tab}
                  onValueChange={(value) => {
                    setTab(value);
                    setPage(1);
                    setAdjustmentPage(1);
                  }}
                  options={titles.map(([value, label]) => ({ value, label }))}
                />
              )}
              {tab === "projects" && (
                <div className="space-y-3">
                  {report.projects.length === 0 ? (
                    <p>Проектов нет.</p>
                  ) : (
                    report.projects
                      .slice((page - 1) * 20, page * 20)
                      .map((p) => (
                        <details
                          key={p.id}
                          className="border rounded-2xl bg-card p-4"
                        >
                          <summary className="cursor-pointer list-none space-y-3">
                            <div className="flex justify-between items-start gap-3">
                              <span className="font-semibold break-words">
                                {p.name}
                                {p.archived_at ? " · архив" : ""}
                              </span>
                              <ChevronDown className="size-4 shrink-0 text-muted-foreground mt-1" />
                            </div>
                            {p.plan ? (
                              <div className="grid grid-cols-2 gap-3 text-xs">
                                <div>
                                  <p className="text-muted-foreground">
                                    Бюджет
                                  </p>
                                  <p className="font-medium mt-1">
                                    {rubles(p.budget)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">
                                    Расходы
                                  </p>
                                  <p className="font-medium mt-1">
                                    {rubles(p.spent)}
                                  </p>
                                </div>
                              </div>
                            ) : (
                              <p className="text-xs text-muted-foreground">
                                Финансовый план не создан
                              </p>
                            )}
                          </summary>
                          <div className="mt-3 space-y-2">
                            <Link
                              className="underline text-sm"
                              to={"/deals/" + p.id + "/show"}
                            >
                              Открыть проект
                            </Link>
                            {p.plan && (
                              <>
                                <p className="text-sm">
                                  План v{p.plan.revision} · КП{" "}
                                  {rubles(p.plan.price)} · Расчётный остаток{" "}
                                  {rubles(p.remainder!)}
                                </p>
                                <DataTable
                                  headings={[
                                    "Зона",
                                    "План",
                                    "Корректировки",
                                    "Бюджет сейчас",
                                    "Расходы",
                                    "Остаток",
                                  ]}
                                  rows={p.rows.map((r) => [
                                    r.label,
                                    rubles(r.base),
                                    rubles(r.change),
                                    rubles(r.budget),
                                    rubles(r.spent),
                                    rubles(r.balance),
                                  ])}
                                />
                              </>
                            )}
                          </div>
                        </details>
                      ))
                  )}
                  <Pager
                    page={page}
                    setPage={setPage}
                    total={report.projects.length}
                  />
                </div>
              )}
              {tab === "zones" && (
                <DataTable
                  headings={[
                    "Зона",
                    "По плану",
                    "Корректировки, нетто",
                    "Бюджет сейчас",
                    "Расходы за всё время",
                    "Остаток",
                    "Расходы за период",
                  ]}
                  rows={FINANCIAL_ZONES.map((z) => {
                    const rows = report.projects.flatMap((p) =>
                      p.rows.filter((r) => r.key === z.key),
                    );
                    const sum = (
                      key: "base" | "change" | "budget" | "spent" | "balance",
                    ) =>
                      rows.reduce((s, r) => s + Math.round(r[key] * 100), 0) /
                      100;
                    return [
                      z.label,
                      rubles(sum("base")),
                      rubles(sum("change")),
                      rubles(sum("budget")),
                      rubles(sum("spent")),
                      rubles(sum("balance")),
                      rubles(
                        report.expenses
                          .filter((e) => e.zone_key === z.key)
                          .reduce(
                            (s, e) => s + Math.round(Number(e.amount) * 100),
                            0,
                          ) / 100,
                      ),
                    ];
                  })}
                />
              )}
              {tab === "reasons" && (
                <div className="space-y-4">
                  <DataTable
                    headings={[
                      "Причина",
                      "Расходы за период",
                      "Заявок",
                      "Переносы бюджета, нетто",
                    ]}
                    rows={report.reasons.map((r) => [
                      reasonLabel(r.reason),
                      rubles(r.expense),
                      r.count,
                      rubles(r.adjustment),
                    ])}
                  />
                  <HelpHint>
                    Расход и перенос бюджета отражают разные операции. Стоимость
                    исправлений видна в подтверждённых расходах с
                    соответствующей причиной.
                  </HelpHint>
                </div>
              )}
              {tab === "parties" && (
                <>
                  <HelpHint>
                    Расходы по зонам относятся к текущему ответственному в плане
                    за всё время. «Кого касается» берётся из корректировки и не
                    означает виновность. Задачи показывают завершения за период;
                    процент своевременности считается только для задач с
                    указанным сроком.
                  </HelpHint>
                  <DataTable
                    headings={[
                      "Сотрудник / подрядчик",
                      "Проектов с зонами",
                      "Расходы зон",
                      "Корректировки, нетто",
                      "Завершено задач",
                      "В срок",
                    ]}
                    rows={report.parties.map((p) => [
                      p.name,
                      p.projects,
                      rubles(p.costs),
                      rubles(p.corrections),
                      p.sales_id != null ? p.completed : "—",
                      p.withDeadline
                        ? p.onTime +
                          " / " +
                          p.withDeadline +
                          " (" +
                          Math.round((p.onTime / p.withDeadline) * 100) +
                          "%)"
                        : "—",
                    ])}
                  />
                </>
              )}
              {tab === "operations" && (
                <>
                  <DataTable
                    headings={[
                      "Дата расхода",
                      "Проект",
                      "Категория / причина",
                      "Зона",
                      "Автор",
                      "Сумма",
                    ]}
                    rows={report.expenses
                      .slice()
                      .sort((a, b) =>
                        b.expense_snapshot.spent_at.localeCompare(
                          a.expense_snapshot.spent_at,
                        ),
                      )
                      .slice((page - 1) * 20, page * 20)
                      .map((e) => [
                        e.expense_snapshot.spent_at,
                        data.projects.find(
                          (p) => String(p.id) === String(e.deal_id),
                        )?.name ?? "#" + e.deal_id,
                        expenseCategoryLabel(e.expense_snapshot.category) +
                          " / " +
                          reasonLabel(e.reason),
                        FINANCIAL_ZONES.find((z) => z.key === e.zone_key)
                          ?.label ?? "—",
                        data.sales.find(
                          (s) =>
                            String(s.id) ===
                            String(e.expense_snapshot.created_by_sales_id),
                        )?.first_name ??
                          "#" + e.expense_snapshot.created_by_sales_id,
                        rubles(e.amount),
                      ])}
                  />
                  <Pager
                    page={page}
                    setPage={setPage}
                    total={report.expenses.length}
                  />
                  <h3 className="font-medium mt-4">
                    Корректировки за выбранный период
                  </h3>
                  <DataTable
                    headings={[
                      "Дата",
                      "Проект",
                      "Причина",
                      "Операция",
                      "Сумма",
                    ]}
                    rows={report.adjustments
                      .slice((adjustmentPage - 1) * 20, adjustmentPage * 20)
                      .map((a) => [
                        new Date(a.created_at).toLocaleString("ru-RU"),
                        data.projects.find(
                          (p) => String(p.id) === String(a.deal_id),
                        )?.name ?? "#" + a.deal_id,
                        reasonLabel(a.reason),
                        a.reverses_id != null
                          ? "Отмена #" + a.reverses_id
                          : a.note,
                        rubles(
                          (a.reverses_id != null ? -1 : 1) * Number(a.amount),
                        ),
                      ])}
                  />
                  <Pager
                    page={adjustmentPage}
                    setPage={setAdjustmentPage}
                    total={report.adjustments.length}
                  />
                </>
              )}
            </>
          )
        )}
      </Content>
    </>
  );
}
function DataTable({
  headings,
  rows,
}: {
  headings: string[];
  rows: (string | number)[][];
}) {
  const isMobile = useIsMobile();
  if (isMobile)
    return rows.length ? (
      <div className="space-y-3">
        {rows.map((row, index) => (
          <article key={index} className="rounded-2xl border bg-card p-4">
            <h3 className="font-semibold text-sm break-words">{row[0]}</h3>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 mt-3">
              {row.slice(1).map((cell, column) => (
                <div
                  key={column}
                  className={
                    String(cell).length > 35 ? "col-span-2" : "min-w-0"
                  }
                >
                  <dt className="text-xs text-muted-foreground">
                    {headings[column + 1]}
                  </dt>
                  <dd className="text-sm font-medium mt-1 break-words tabular-nums">
                    {cell}
                  </dd>
                </div>
              ))}
            </dl>
          </article>
        ))}
      </div>
    ) : (
      <p className="rounded-xl border bg-card p-4 text-sm text-muted-foreground">
        Данных пока нет.
      </p>
    );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left">
            {headings.map((h) => (
              <th key={h} className="p-2 whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map((r, i) => (
              <tr key={i} className="border-b">
                {r.map((cell, j) => (
                  <td key={j} className="p-2">
                    {cell}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={headings.length}
                className="p-4 text-muted-foreground"
              >
                Данных пока нет.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
function Pager({
  page,
  setPage,
  total,
}: {
  page: number;
  setPage: (p: number) => void;
  total: number;
}) {
  return total > 20 ? (
    <div className="flex gap-3 items-center mt-3">
      <Button
        variant="outline"
        disabled={page === 1}
        onClick={() => setPage(page - 1)}
      >
        Назад
      </Button>
      <span>Страница {page}</span>
      <Button
        variant="outline"
        disabled={page * 20 >= total}
        onClick={() => setPage(page + 1)}
      >
        Далее
      </Button>
    </div>
  ) : null;
}
